import React, { useRef, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Preview } from "@creatomate/preview";
import { generateNearlyUniqueID } from "common/uuid";
import eventBus from "../controls/event-bus";

const propTypes = {
  creative: PropTypes.shape({
    renderedJsonDocument: PropTypes.string,
    width: PropTypes.number.isRequired,
    height: PropTypes.number.isRequired
  }),
  width: PropTypes.number,
  height: PropTypes.number,
  autoPlay: PropTypes.bool,
  outputFormat: PropTypes.string
};

const DEFAULT_CREATIVE = {
  renderedJsonDocument: '{"elements": []}', // Dummy initial doc
  width: 300,
  height: 250
};

const CreatomateAdPreview = ({
  creative = DEFAULT_CREATIVE,
  width = undefined,
  height = undefined,
  autoPlay = false,
  outputFormat = null
}) => {
  const uuid = generateNearlyUniqueID();
  const { renderedJsonDocument } = creative;
  const [composition, setComposition] = useState(JSON.parse(renderedJsonDocument || "{}"));
  const previewWidth = width || creative.width;
  const previewHeight = height || creative.height;
  const latestPreviewSize = useRef({ width: previewWidth, height: previewHeight });

  const previewRef = useRef();
  const timerRef = useRef(null);
  const compositionRef = useRef(composition); // Create a ref for the latest composition

  useEffect(() => {
    latestPreviewSize.current = { width: previewWidth, height: previewHeight };
    const iframeNode = previewRef.current?.element?.querySelector("iframe");
    if (iframeNode) {
      iframeNode.style.width = `${latestPreviewSize.current.width}px`;
      iframeNode.style.height = `${latestPreviewSize.current.height}px`;
    }
  }, [previewWidth, previewHeight]);

  const playVideo = async () => {
    await previewRef.current?.play();
  };

  const setUpPreview = htmlElement => {
    if (previewRef.current) {
      previewRef.current.dispose();
      previewRef.current = undefined;
    }

    const preview = new Preview(htmlElement, "player", "public-9mq9l33kub8r3g2qjf0vvb3n");

    preview.onLoad = () => {
      // Set a timer to check if the preview is taking too long to load
      if (timerRef.current === null) {
        timerRef.current = setTimeout(() => {
          console.error("Preview loading is taking too long.");
          preview.dispose();
          setUpPreview(htmlElement);
        }, 5000); // 5 seconds
      }
    };

    preview.onLoadComplete = () => {
      // Clear our timer if the load is complete
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }

      if (autoPlay && previewRef.current) {
        playVideo();
      }
    };

    preview.onReady = async () => {
      const currentComposition = compositionRef.current; // Use the ref to get the latest composition
      await preview.setSource(currentComposition);

      // Start the video from the beginning
      await preview.setTime(0);

      if (!autoPlay) {
        await preview.pause();
      }

      let attempts = 0;
      const checkIframeNode = setInterval(() => {
        const iframeNode = previewRef.current?.element?.querySelector("iframe");

        if (iframeNode) {
          iframeNode.style.width = `${latestPreviewSize.current.width}px`;
          iframeNode.style.height = `${latestPreviewSize.current.height}px`;
          clearInterval(checkIframeNode);
        } else {
          attempts += 1;
          if (attempts >= 10) {
            console.error("Failed to load preview iframe after 10 attempts");
            clearInterval(checkIframeNode);
          } else {
            console.log("Waiting for preview iframe to load");
          }
        }
      }, 200);
    };

    previewRef.current = preview;
  };

  const setSource = async (source, refreshPreview) => {
    await previewRef.current?.setSource(source);
    if (refreshPreview) {
      await previewRef.current?.setTime(0);
      // Begin playing the ad again from the beginning
      await previewRef.current?.play();
    }
  };

  useEffect(() => {
    compositionRef.current = outputFormat
      ? { ...composition, output_format: outputFormat }
      : composition;
  }, [composition, outputFormat]);

  useEffect(() => {
    const dataUpdateListener = data => {
      setComposition(data.source);
      setSource(data.source, data.refreshPreview);
    };
    eventBus.on("creatomateSourceUpdated", dataUpdateListener);

    return () => {
      eventBus.removeListener("creatomateSourceUpdated", dataUpdateListener);
    };
  }, []);

  useEffect(() => {
    return () => {
      // Clear our timer if the load is complete
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, []);

  return (
    <div
      id={`creatomate-preview-${uuid}`}
      data-testid="creatomate-ad-preview"
      data-width={previewWidth}
      data-height={previewHeight}
    >
      <div
        ref={htmlElement => {
          if (htmlElement && htmlElement !== previewRef.current?.element) {
            setUpPreview(htmlElement);
          }
        }}
      />
    </div>
  );
};

CreatomateAdPreview.propTypes = propTypes;

export default CreatomateAdPreview;
